import { createAsyncAction, ActionType, createAction } from 'typesafe-actions';
import { FetchAction, SuccessFetchActionPayload, DefaultErrorPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { PriceFormatConfigOptions, ProductInterface } from '../products/types';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { PDPApiResponse, PdpRequestQueryParam, ProductSimpleVideoResponse, TrackingVideoActions } from './types';

export const changeProductDetailSizeAction = createAction('@PRODUCT_DETAILS/CHANGE_SIZE', action => {
    return (simple: string) => action({ simple });
});

export const setTrackingFlag = createAction('@PRODUCT_DETAILS/SET_TRACKING_FLAG', action => {
    return (event: TrackingVideoActions, percent?: number) => action({ event, percent });
});

export const setPDPProductKnownInfoAction = createAction('@PRODUCT_DETAILS/SET_INFO', action => {
    return (product: ProductInterface) => action({ product });
});

export const setPDPVimeoStatusAction = createAction('@PRODUCT_DETAILS/SET_VIMEO_STATUS', action => {
    return (isVimeoVideoPlayerEnabled: boolean) => action({ isVimeoVideoPlayerEnabled });
});

export interface FetchProductDetailsActions extends SuccessFetchActionPayload<PDPApiResponse> {
    isVimeoVideoPlayerEnabled: boolean;
}

export const fetchProductDetailsActions = createAsyncAction(
    '@PRODUCT_DETAILS/FETCH_START',
    '@PRODUCT_DETAILS/FETCH_SUCCESS',
    '@PRODUCT_DETAILS/FETCH_FAILED'
)<FetchAction, FetchProductDetailsActions, DefaultErrorPayload>();

export const fetchProductDetailsIfNeeded = (
    urlKey: string,
    queryParams: PdpRequestQueryParam,
    reducerPayloadOptions: PriceFormatConfigOptions,
    isVimeoVideoPlayerEnabled: boolean
    // eslint-disable-next-line max-params
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    const identifier = urlKey;
    if (identifier === getState().productdetails.identifier) {
        if (isVimeoVideoPlayerEnabled !== getState().productdetails.isVimeoVideoPlayerEnabled) {
            dispatch(setPDPVimeoStatusAction(isVimeoVideoPlayerEnabled));
        }
        return;
    }
    dispatch(fetchProductDetailsActions.request({ identifier }));
    try {
        const productsDetails = await apiService.shopApi.getProductsForDetailPage(urlKey, queryParams);
        if (identifier !== getState().productdetails.identifier) {
            return;
        }
        dispatch(
            fetchProductDetailsActions.success({
                data: productsDetails,
                identifier,
                reducerPayloadOptions,
                isVimeoVideoPlayerEnabled,
            })
        );
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductsDetailsIfNeeded');
        dispatch(fetchProductDetailsActions.failure({ error: err, identifier }));
    }
};

export const fetchProductSimpleVideoActions = createAsyncAction(
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_START',
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_SUCCESS',
    '@PRODUCT_DETAILS_SIMPLE_VIDEO/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ProductSimpleVideoResponse>, DefaultErrorPayload>();

export const fetchProductSimpleVideoIfNeeded = (urlKey: string, simpleSku: string): DataFetchingThunkAction => async (
    dispatch,
    getState,
    { apiService, logger }
) => {
    const identifier = simpleSku;
    if (!identifier) {
        return;
    }
    dispatch(fetchProductSimpleVideoActions.request({ identifier }));
    try {
        const video = await apiService.shopApi.getProductVideoBySimpleSku(urlKey, { simpleSku });

        dispatch(
            fetchProductSimpleVideoActions.success({
                data: video,
                identifier,
            })
        );
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductSimpleVideoIfNeeded');
        dispatch(fetchProductSimpleVideoActions.failure({ error: err, identifier }));
    }
};

export type ProductDetailsActionsType =
    | ActionType<typeof fetchProductDetailsActions>
    | ActionType<typeof fetchProductSimpleVideoActions>
    | ActionType<typeof changeProductDetailSizeAction>
    | ActionType<typeof setPDPProductKnownInfoAction>
    | ActionType<typeof setPDPVimeoStatusAction>
    | ActionType<typeof setTrackingFlag>;
